import {
  BOARDING_SEGMENT_LEGACY_PICKUP_EXCLUSIVE_END_OFFSET,
  MINIMAL_STATIONS_COUNT_FOR_BOARDING_SEGMENT_DURATION,
} from "../../constants";
import { DirectionEnum } from "../../enums";
import { durationInMinutesCeil } from "../formula.helpers";
import {
  isValidPositiveNumber,
  safeParseNumericStringOrUndefined,
} from "../numbers.helpers";

const parsePositiveDurationMinutes = (value: unknown): number | undefined => {
  const parsed = safeParseNumericStringOrUndefined(value);
  if (parsed === undefined || parsed <= 0) {
    return undefined;
  }
  return parsed;
};

const sumDurationLegs = ({
  durations,
  startStationIndex,
  endStationIndex,
}: {
  durations: number[];
  startStationIndex: number;
  endStationIndex: number;
}): number => {
  if (endStationIndex <= startStationIndex) {
    return 0;
  }
  let sum = 0;
  for (let i = startStationIndex; i < endStationIndex; i++) {
    const leg = durations[i];
    if (typeof leg !== "number" || !Number.isFinite(leg)) {
      return 0;
    }
    sum += leg;
  }
  return sum;
};

type TRideBoardingSegmentStation = { id?: number };
type TRideBoardingSegmentPassenger = {
  from?: number | null;
  to?: number | null;
};

export const MIN_PASSENGER_BOARDING_OR_ALIGHTING_STATIONS_FOR_BOARDING_SEGMENT_INDICATOR = 2;

export const countPassengerBoardingOrAlightingStations = ({
  metadata,
  direction,
}: {
  metadata?: { passengersInfo?: TRideBoardingSegmentPassenger[] } | null;
  direction?: DirectionEnum;
}): number => {
  const passengers = metadata?.passengersInfo?.filter(Boolean) ?? [];
  if (!passengers.length || !direction) {
    return 0;
  }
  const stationIds = new Set<number>();
  if (direction === DirectionEnum.PickUp) {
    passengers.forEach(passenger => {
      if (isValidPositiveNumber(passenger.from)) {
        stationIds.add(passenger.from);
      }
    });
  } else if (direction === DirectionEnum.DropOff) {
    passengers.forEach(passenger => {
      if (isValidPositiveNumber(passenger.to)) {
        stationIds.add(passenger.to);
      }
    });
  }
  return stationIds.size;
};

/**
 * UI indicator: shown only when passengers define a meaningful boarding segment
 * (not the no-passenger fallback used elsewhere in production logic).
 */
export const isRideBoardingSegmentDurationIndicatorVisible = ({
  metadata,
  direction,
}: {
  metadata?: {
    stationsInfo?: TRideBoardingSegmentStation[];
    durations?: number[];
    passengersInfo?: TRideBoardingSegmentPassenger[];
  } | null;
  direction?: DirectionEnum;
}): boolean => {
  if (!direction || direction === DirectionEnum.Both) {
    return false;
  }
  const passengers = metadata?.passengersInfo?.filter(Boolean) ?? [];
  if (!passengers.length) {
    return false;
  }
  if (
    countPassengerBoardingOrAlightingStations({ metadata, direction }) <
    MIN_PASSENGER_BOARDING_OR_ALIGHTING_STATIONS_FOR_BOARDING_SEGMENT_INDICATOR
  ) {
    return false;
  }
  return getRideBoardingSegmentDurationSeconds({ metadata, direction }) > 0;
};

/**
 * With passengers: PickUp is first station → last station someone boards;
 * DropOff is first station someone alights → last station.
 * Without passengers: PickUp excludes the final leg, DropOff excludes the first leg.
 */
export const getRideBoardingSegmentDurationSeconds = ({
  metadata,
  direction,
}: {
  metadata?: {
    stationsInfo?: TRideBoardingSegmentStation[];
    durations?: number[];
    passengersInfo?: TRideBoardingSegmentPassenger[];
  } | null;
  direction?: DirectionEnum;
}): number => {
  const stations = metadata?.stationsInfo;
  const durations = metadata?.durations;
  if (
    !stations?.length ||
    !durations?.length ||
    stations.length !== durations.length + 1
  ) {
    return 0;
  }

  const passengers = metadata?.passengersInfo?.filter(Boolean) ?? [];
  if (passengers.length) {
    if (direction === DirectionEnum.PickUp) {
      let lastBoardingIndex = -1;
      stations.forEach((station, index) => {
        if (
          passengers.some(
            passenger =>
              isValidPositiveNumber(passenger.from) &&
              passenger.from === station.id,
          )
        ) {
          lastBoardingIndex = index;
        }
      });
      if (lastBoardingIndex < 0) {
        return 0;
      }
      return sumDurationLegs({
        durations,
        startStationIndex: 0,
        endStationIndex: lastBoardingIndex,
      });
    }
    if (direction === DirectionEnum.DropOff) {
      const firstDropoffIndex = stations.findIndex(station =>
        passengers.some(
          passenger =>
            isValidPositiveNumber(passenger.to) && passenger.to === station.id,
        ),
      );
      if (firstDropoffIndex < 0) {
        return 0;
      }
      return sumDurationLegs({
        durations,
        startStationIndex: firstDropoffIndex,
        endStationIndex: stations.length - 1,
      });
    }
    return 0;
  }

  if (stations.length < MINIMAL_STATIONS_COUNT_FOR_BOARDING_SEGMENT_DURATION) {
    return 0;
  }

  if (direction === DirectionEnum.PickUp) {
    return sumDurationLegs({
      durations,
      startStationIndex: 0,
      endStationIndex:
        stations.length - BOARDING_SEGMENT_LEGACY_PICKUP_EXCLUSIVE_END_OFFSET,
    });
  }
  if (direction === DirectionEnum.DropOff) {
    return sumDurationLegs({
      durations,
      startStationIndex: 1,
      endStationIndex: stations.length - 1,
    });
  }
  return 0;
};

export const getGeoProdMaxSegmentDurationMinutesByDirectionFromSystemParams = ({
  systemParams,
  direction,
}: {
  systemParams?: {
    geoProdMaxDurationPickupToPickup?: number | string | null;
    geoProdMaxDurationDropOffToDropOff?: number | string | null;
  } | null;
  direction?: DirectionEnum;
}): number | undefined => {
  if (direction === DirectionEnum.PickUp) {
    return parsePositiveDurationMinutes(
      systemParams?.geoProdMaxDurationPickupToPickup,
    );
  }
  if (direction === DirectionEnum.DropOff) {
    return parsePositiveDurationMinutes(
      systemParams?.geoProdMaxDurationDropOffToDropOff,
    );
  }
  return undefined;
};

export const isRideBoardingSegmentDurationLimitConfigured = ({
  priceRateMaxBoardingSegmentDurationInMinutes,
  systemParams,
  direction,
}: {
  priceRateMaxBoardingSegmentDurationInMinutes?: number | null;
  systemParams?: {
    geoProdMaxDurationPickupToPickup?: number | string | null;
    geoProdMaxDurationDropOffToDropOff?: number | string | null;
  } | null;
  direction?: DirectionEnum;
}): boolean => {
  if (
    parsePositiveDurationMinutes(
      priceRateMaxBoardingSegmentDurationInMinutes,
    ) !== undefined
  ) {
    return true;
  }
  return (
    getGeoProdMaxSegmentDurationMinutesByDirectionFromSystemParams({
      systemParams,
      direction,
    }) !== undefined
  );
};

export const getEffectiveMaxSegmentDurationMinutes = ({
  priceRateMaxBoardingSegmentDurationInMinutes,
  systemParams,
  direction,
}: {
  priceRateMaxBoardingSegmentDurationInMinutes?: number | null;
  systemParams?: {
    geoProdMaxDurationPickupToPickup?: number | string | null;
    geoProdMaxDurationDropOffToDropOff?: number | string | null;
  } | null;
  direction?: DirectionEnum;
}): number | undefined => {
  const fromRate = parsePositiveDurationMinutes(
    priceRateMaxBoardingSegmentDurationInMinutes,
  );
  if (fromRate !== undefined) {
    return fromRate;
  }
  return getGeoProdMaxSegmentDurationMinutesByDirectionFromSystemParams({
    systemParams,
    direction,
  });
};

export const getRideBoardingSegmentDurationMinutesCeil = ({
  metadata,
  direction,
}: {
  metadata?: {
    stationsInfo?: TRideBoardingSegmentStation[];
    durations?: number[];
    passengersInfo?: TRideBoardingSegmentPassenger[];
  } | null;
  direction?: DirectionEnum;
}): number =>
  durationInMinutesCeil(
    getRideBoardingSegmentDurationSeconds({ metadata, direction }),
  ) ?? 0;
